import React from "react";
import { Link } from "react-router-dom";
import { EmptyState, PortalCard, PortalLoading, StatusBadge, money, niceDate, useStudentPortal } from "../StudentPortal/studentPortalUtils";

function StudentDashboard() {
  const { data, loading, reload } = useStudentPortal("student-portal/dashboard/");

  if (loading && !data) return <PortalLoading />;

  const student = data?.student || {};
  const fee = data?.fee_status || {};
  const timetable = data?.today_timetable || [];
  const resources = data?.learning?.resources || [];
  const liveClasses = data?.learning?.live_classes || [];
  const resultSessions = data?.results?.sessions || [];
  const borrowings = data?.library?.borrowings || [];
  const projects = data?.ict_robotics?.projects || [];
  const events = data?.events_sports?.events || [];
  const fixtures = data?.events_sports?.fixtures || [];
  const conduct = data?.conduct?.summary || {};

  return (
    <div className="container-fluid py-4">
      <div className="card border-0 shadow-sm mb-3 overflow-hidden">
        <div className="card-body p-4" style={{ background: "linear-gradient(135deg, #eef8f1, #ffffff)" }}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <small className="text-uppercase fw-bold text-success">Student Portal</small>
              <h3 className="fw-bold mb-1" style={{ color: "#125b2d" }}>Welcome, {student.first_name || student.full_name || "Student"}</h3>
              <p className="text-muted mb-0">
                {student.admission_number || "No admission number"} · {student.class_group?.name || "No class group assigned"}
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button className="btn btn-light border" onClick={reload}><i className="bi bi-arrow-clockwise me-2"></i>Refresh</button>
              <Link className="btn btn-success" to="/student/profile"><i className="bi bi-person-circle me-2"></i>My Profile</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <PortalCard title="Fee Access" icon="bi-shield-check" to="/student/finance">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <StatusBadge value={fee.status_label || "Unknown"} />
                <div className="small text-muted mt-2">Outstanding balance</div>
                <h4 className="fw-bold mb-0">{money(fee.outstanding_balance)}</h4>
              </div>
              <i className="bi bi-cash-coin fs-1 text-success"></i>
            </div>
            <p className="small text-muted mt-3 mb-0">{fee.message}</p>
          </PortalCard>
        </div>
        <div className="col-md-4">
          <PortalCard title="Today’s Lessons" icon="bi-calendar2-week" to="/student/timetable">
            <h4 className="fw-bold mb-1">{timetable.length}</h4>
            <p className="text-muted small mb-0">lesson/period item{timetable.length === 1 ? "" : "s"} scheduled today.</p>
          </PortalCard>
        </div>
        <div className="col-md-4">
          <PortalCard title="Conduct Status" icon="bi-shield-check" to="/student/conduct">
            <h4 className="fw-bold mb-1">{conduct.open_incidents || 0}</h4>
            <p className="text-muted small mb-0">open conduct follow-up{conduct.open_incidents === 1 ? "" : "s"}.</p>
          </PortalCard>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-xl-6">
          <PortalCard title="Today’s Timetable" icon="bi-clock" to="/student/timetable">
            {timetable.length === 0 ? <EmptyState title="No timetable today" message="No active periods found for today." /> : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <tbody>
                    {timetable.slice(0, 6).map((slot) => (
                      <tr key={slot.id}>
                        <td className="fw-semibold">{slot.period}</td>
                        <td>{slot.subject}</td>
                        <td className="text-muted small">{slot.teacher || slot.room || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PortalCard>
        </div>
        <div className="col-xl-6">
          <PortalCard title="Latest Learning" icon="bi-play-circle" to="/student/learning">
            {resources.length === 0 ? <EmptyState title="No resources yet" message="Published resources will appear here." /> : resources.map((item) => (
              <div className="border-bottom py-2" key={item.id}>
                <div className="fw-semibold">{item.title}</div>
                <small className="text-muted">{item.subject || "General"} · {item.resource_type}</small>
                {!item.can_view && <div className="small text-warning">{item.restricted_message}</div>}
              </div>
            ))}
          </PortalCard>
        </div>
        <div className="col-xl-6">
          <PortalCard title="Results" icon="bi-journal-check" to="/student/results">
            {resultSessions.length === 0 ? <EmptyState title="No published results" message="Published report sessions will appear here." /> : resultSessions.map((session) => (
              <div className="d-flex justify-content-between border-bottom py-2" key={session.id}>
                <div><div className="fw-semibold">{session.name}</div><small className="text-muted">{session.marks_count} mark records</small></div>
                <StatusBadge value={session.can_view ? "Available" : "Restricted"} />
              </div>
            ))}
          </PortalCard>
        </div>
        <div className="col-xl-6">
          <PortalCard title="ICT & Robotics" icon="bi-cpu" to="/student/ict-robotics">
            {projects.length === 0 ? <EmptyState title="No projects" message="Active ICT and Robotics projects will appear here." /> : projects.map((project) => (
              <div className="d-flex justify-content-between border-bottom py-2" key={project.id}>
                <div><div className="fw-semibold">{project.title}</div><small className="text-muted">Due {niceDate(project.due_date)}</small></div>
                <StatusBadge value={project.submission_status} />
              </div>
            ))}
          </PortalCard>
        </div>
        <div className="col-xl-4">
          <PortalCard title="Library" icon="bi-book" to="/student/library">
            <h5 className="fw-bold">{data?.library?.borrowed_count || 0} borrowed</h5>
            <p className="small text-muted mb-0">{data?.library?.overdue_count || 0} overdue item(s).</p>
            {borrowings.slice(0, 3).map((item) => <div className="small border-top py-2" key={item.id}>{item.title}</div>)}
          </PortalCard>
        </div>
        <div className="col-xl-4">
          <PortalCard title="Live Classes" icon="bi-camera-video" to="/student/learning">
            {liveClasses.length === 0 ? <EmptyState title="No live classes" message="Upcoming live sessions will appear here." /> : liveClasses.map((item) => <div className="small border-bottom py-2" key={item.id}>{item.title}<br/><span className="text-muted">{item.subject}</span></div>)}
          </PortalCard>
        </div>
        <div className="col-xl-4">
          <PortalCard title="Events & Sports" icon="bi-calendar-event" to="/student/events-sports">
            {[...events, ...fixtures].length === 0 ? <EmptyState title="No announcements" message="Published events and fixtures will appear here." /> : [...events, ...fixtures].slice(0, 4).map((item, index) => <div className="small border-bottom py-2" key={`${item.id}-${index}`}>{item.title}</div>)}
          </PortalCard>
        </div>
      </div>
    </div>
  );
}

export default StudentDashboard;
